import { useEffect, useState, useMemo } from 'react';
import { Box, Center, Spinner, SimpleGrid, Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  TableOptions,
  DataTable,
  Pagination,
  StatusBadge,
  type ToggleOption,
  type Column,
  type StatusType,
} from '@themes/aegix';
import { UserStatus } from '../../services';
import { DrawerComponent } from '../ui/drawerComponent';
import { Button as ButtonComponent } from '@themes/aegix';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import UsersForm from '../forms/UsersForm';
import { userFormValidationSchema, createUserFormValidationSchema } from '../../validations/user.validation';
import { toaster } from '../ui/toaster';
import { DialogComponent } from '@themes/aegix';
import { ResetPassowordForm } from '../forms/ResetPassowordForm';
import { passwordSchema } from '../../validations/password.validation';
import { useUsersContentData, type UserRow } from './hooks/useUsersContentData';
import { BulkInviteDialog } from './BulkInviteDialog';
export type { UserRow } from './hooks/useUsersContentData';

export function UsersContent() {
  const { t } = useTranslation();
  const {
    drawerOpen,
    bulkInviteOpen,
    setBulkInviteOpen,
    currentRow,
    statusFilter,
    setStatusFilter,
    searchValue,
    setSearchValue,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    actionMode,
    sortColumn,
    sortDirection,
    filterValues,
    actionDialog,
    setActionDialog,
    drawerContentRef,
    users,
    filteredUsers,
    hasActiveFilters,
    totalItems,
    totalPages,
    isLoading,
    error,
    userActivityDetail,
    userFormDefaultValues,
    handleSort,
    handleFilterChange,
    handleFilterClear,
    handleDrawerClose,
    handleSubmit,
    handleResetPassword,
    openEditDrawer,
    openAddDrawer,
  } = useUsersContentData();

  const userToggleOptions: ToggleOption[] = useMemo(
    () => [
      { value: 'all', label: t('common.all', 'All'), 'data-e2e': 'user-filter-all' },
      { value: 'active', label: t('common.active', 'Active'), 'data-e2e': 'user-filter-active' },
      { value: 'inactive', label: t('common.inactive', 'Inactive'), 'data-e2e': 'user-filter-inactive' },
    ],
    [t],
  );

  const columns: Column<UserRow>[] = useMemo(
    () => [
      {
        key: 'name',
        label: t('common.name', 'Name'),
        sortable: true,
        filterable: true,
        width: '220px',
      },
      {
        key: 'email',
        label: t('common.email', 'Email'),
        sortable: true,
        filterable: true,
        width: '280px',
      },
      {
        key: 'role',
        label: t('common.role', 'Role'),
        filterable: true,
        width: '180px',
      },
      {
        key: 'status',
        label: t('common.status', 'Status'),
        sortable: true,
        filterable: true,
        filterOptions: [
          { label: t('common.active', 'Active'), value: 'active' },
          { label: t('common.inactive', 'Inactive'), value: 'inactive' },
        ],
        width: '120px',
        render: (row) => (
          <StatusBadge
            status={row.status}
            label={t(`common.${row.status}`, row.status)}
          />
        ),
      },
    ],
    [t],
  );

  const userFormMethods = useForm({
    defaultValues: userFormDefaultValues,
    resolver: zodResolver(actionMode === 'add' ? createUserFormValidationSchema : userFormValidationSchema),
    mode: 'onSubmit',
  });

  useEffect(() => {
    userFormMethods.reset(userFormDefaultValues);
  }, [userFormDefaultValues, userFormMethods]);

  const resetPasswordMethods = useForm({
    defaultValues: {},
    resolver: zodResolver(passwordSchema),
  });

  return (
    <>
      <TableOptions
        toggleOptions={userToggleOptions}
        activeToggle={statusFilter}
        onToggleChange={(v: string) => {
          setStatusFilter(v as UserStatus);
          setCurrentPage(1);
        }}
        searchValue={searchValue}
        onSearchChange={(v) => {
          setSearchValue(v);
          setCurrentPage(1);
        }}
        searchPlaceholder={t('common.searchPlaceholder', 'Search...')}
        secondaryAction={{
          label: t('users.bulkUpload', 'Bulk Upload'),
          onClick: () => setBulkInviteOpen(true),
          'data-e2e': 'bulk-upload-users-btn',
        }}
        primaryAction={{
          label: t('users.addUser', 'Add User'),
          onClick: openAddDrawer,
        }}
      />

      <Box px={5} pb={5}>
        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : error ? (
          <Center py={12}>
            <Text color="text.danger">
              {error instanceof Error ? error.message : t('users.failedLoad', 'Failed to load users')}
            </Text>
          </Center>
        ) : users.length === 0 && !hasActiveFilters ? (
          <Center py={12}>
            <Text color="text.secondary">{t('users.noUsers', 'No users found')}</Text>
          </Center>
        ) : (
          <>
            <DataTable
              columns={columns}
              data={filteredUsers}
              keyExtractor={(row) => row.id}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
              onFilterClear={handleFilterClear}
              onRowClick={(row) => openEditDrawer(row)}
              emptyMessage="No users found"
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}

        <DrawerComponent
          isOpen={drawerOpen}
          onClose={() => handleDrawerClose()}
          title={actionMode === 'add' ? t('users.addNewUser', 'Add New User') : t('users.editUser', 'Edit User')}
          size="lg"
          closeOnOutside={false}
          closeOnEsc={false}
          drawerContentRef={drawerContentRef}
          footer={
            <SimpleGrid
              columns={actionMode === 'edit' ? 3 : 2}
              gap={4}
              width={'100%'}
            >
              {actionMode === 'edit' && (
                <ButtonComponent
                  visual="alert"
                  type="button"
                  onClick={() => handleDrawerClose()}
                  data-e2e="delete-button">
                  {t('common.delete', 'Delete')}
                </ButtonComponent>
              )}
              <ButtonComponent
                visual="secondary"
                type="button"
                onClick={() => handleDrawerClose()}
                data-e2e="cancel-button">
                {t('common.cancel', 'Cancel')}
              </ButtonComponent>
              <ButtonComponent visual="primary" type="submit" form="users-form" data-e2e="save-button">
                {t('common.save', 'Save')}
              </ButtonComponent>
            </SimpleGrid>
          }
        >
          <FormProvider {...userFormMethods}>
            <form id="users-form" onSubmit={userFormMethods.handleSubmit(handleSubmit)}>
              <UsersForm
                setActionDialog={setActionDialog}
              />
            </form>
          </FormProvider>

          <DialogComponent
            e2eTitle="ResetPassword"
            isOpen={actionDialog.open}
            onClose={() =>
              setActionDialog({
                action: '',
                open: false,
              })
            }
            headingText={
              actionDialog.action === 'reset'
                ? t('users.resetPassword', 'Reset Password')
                : t('users.activityLog', 'Activity Log')
            }
          >
            <Box>
              {actionDialog.action === 'reset' ? (
                <FormProvider {...resetPasswordMethods}>
                  <form id="reset-password-form" onSubmit={resetPasswordMethods.handleSubmit(handleResetPassword)}>
                    <ResetPassowordForm />
                    <SimpleGrid columns={2} gap={4} width={'100%'} mt={4}>
                      <ButtonComponent
                        visual="secondary"
                        type="button"
                        onClick={() =>
                          setActionDialog({
                            action: '',
                            open: false,
                          })
                        }
                        data-e2e="cancel-button">
                        {t('common.cancel', 'Cancel')}
                      </ButtonComponent>
                      <ButtonComponent
                        visual="primary"
                        type="submit"
                        form="reset-password-form"
                        data-e2e="reset-password-button">
                        {t('users.resetPassword', 'Reset Password')}
                      </ButtonComponent>
                    </SimpleGrid>
                  </form>
                </FormProvider>
              ) : (
                <Flex
                  height={'50vh'}
                  overflow={'hidden'}
                  overflowY={'auto'}
                  flexDirection={'column'}
                  gap={4}
                >
                  {userActivityDetail?.results && userActivityDetail.results.length > 0 ? (
                    userActivityDetail.results.map((list: any, index: number) => {
                      return (
                        <Box
                          border="1px solid "
                          borderColor={'border.tertiary'}
                          data-e2e="card"
                          padding={'12px'}
                          borderRadius={'8px'}
                          key={index}
                        >
                          <SimpleGrid columns={2}>
                            <Box>
                              <Text
                                color={'text.primary'}
                                fontSize={'15px'}
                                fontWeight={600}
                              >
                                {list.device || t('users.unknownDevice', 'Unknown Device')}
                              </Text>
                              {list.ip && (
                                <Text
                                  color={'text.secondary'}
                                  fontSize={'13px'}
                                  fontWeight={400}
                                  pt={1}
                                >
                                  {t('users.ipLabel', 'IP: {{ip}}', { ip: list.ip })}
                                </Text>
                              )}
                            </Box>
                            <Text
                              textAlign={'end'}
                              color={'text.tertiary'}
                              fontSize={'12px'}
                              fontWeight={400}
                            >
                              {list.created
                                ? new Date(list.created).toLocaleString()
                                : list.timestamp
                                  ? new Date(list.timestamp).toLocaleString()
                                  : ''}
                            </Text>
                          </SimpleGrid>
                        </Box>
                      );
                    })
                  ) : (
                    <Center py={10}>
                      <Text color="text.secondary" fontSize="sm">
                        {t('users.noActivityLogs', 'No activity logs found.')}
                      </Text>
                    </Center>
                  )}
                </Flex>
              )}
            </Box>
          </DialogComponent>
        </DrawerComponent>
      </Box>
      <BulkInviteDialog
        isOpen={bulkInviteOpen}
        onClose={() => setBulkInviteOpen(false)}
      />
    </>
  );
}
